import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { fmt } from '@nyce/admin-kit';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page, TableScroller } from '../../components/common';
import { BarChart } from '@nyce/admin-kit/console';
import { KpiGrid, LimitMeter, buddyPath, compact } from '../shared';
import { IssueList, Kpi } from '../../pages/dashboard/shared';
import { buddyIssues, useBuddySummary, type BuddySummary } from './summary';
import '../../pages/statistics/statistics.css';

const TOP = 8;

function EnvironmentSection({ s }: { s: BuddySummary }) {
  const navigate = useNavigate();
  const link = (path: string) => buddyPath(path, s.target.id);
  const u = s.usage;
  const systems = s.health?.filter((h) => h.enabled) ?? [];
  const up = systems.filter((h) => h.status === 'ok').length;
  const top = [...(u?.groups ?? [])].sort((a, b) => b.weightedTokens - a.weightedTokens).slice(0, TOP);
  return (
    <section
      className="oph-dash-section"
      aria-label={`nyce.buddy ${s.target.label}`}
      data-target={s.target.id}
    >
      <div className="oph-dash-section-head">
        <h2>
          {s.target.label} <span className="nyk-ui-hint">{s.target.buddyUrl}</span>
        </h2>
        <Chip size="sm" className={s.signedIn ? 'nyk-tone-green' : 'nyk-tone-grey'}>
          {s.signedIn ? 'signed in' : 'signed out'}
        </Chip>
      </div>
      {!s.signedIn ? (
        <EmptyState
          card
          title={`Not signed in to ${s.target.label}`}
          hint={
            s.error && s.error !== 'not signed in'
              ? s.error
              : 'Open a Buddy page of this environment to sign in.'
          }
          action={
            <Button size="sm" variant="ghost" onClick={() => navigate(link('/buddy/usage'))}>
              Sign in
            </Button>
          }
        />
      ) : !u ? (
        <ErrorBox error={s.error} />
      ) : (
        <>
          {s.partErrors.length > 0 && <ErrorBox error={s.partErrors.join(' · ')} />}
          <p className="nyk-ui-hint">Last 30 days, compared with the 30 days before.</p>
          <KpiGrid totals={u.totals} compare core />
          <div className="nyk-kpis">
            {s.health && (
              <Kpi
                label="Systems"
                value={`${up} / ${systems.length}`}
                sub="available"
                tone={up < systems.length ? 'red' : undefined}
              />
            )}
            {s.feedback && (
              <Kpi
                label="Feedback to triage"
                value={fmt(s.feedback.unhandled)}
                sub={`${fmt(s.feedback.down)} thumbs down in total`}
                tone={s.feedback.unhandled > 0 ? 'amber' : undefined}
              />
            )}
            {s.alerts && (
              <Kpi
                label="Open monitor alerts"
                value={fmt(s.alerts.length)}
                tone={s.alerts.length > 0 ? 'amber' : undefined}
              />
            )}
          </div>
          <div className="bd-grid">
            <BarChart
              title="Questions per day"
              bars={(u.daily ?? []).map((d) => ({ key: d.date, label: d.date, v: d.questions ?? 0 }))}
              format={(v) => fmt(v)}
            />
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Top organizations</h2>
                <Link to={link('/buddy/usage?groupBy=organization&period=30d')} className="nyk-ui-hint">
                  all {u.groups.length}
                </Link>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="buddy-top-orgs">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th className="nyk-ui-num">Questions</th>
                      <th className="nyk-ui-num">NYCE tokens</th>
                      <th>Month limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((g) => (
                      <tr key={g.id}>
                        <td className="nyk-card-title">
                          <Link
                            to={link(
                              `/buddy/usage?groupBy=organization&period=30d&drill=organization:${encodeURIComponent(g.id)}`
                            )}
                          >
                            {g.name}
                          </Link>
                        </td>
                        <td className="nyk-ui-num" data-label="Questions">
                          {fmt(g.questions)}
                        </td>
                        <td className="nyk-ui-num" data-label="NYCE tokens">
                          {compact(g.weightedTokens)}
                        </td>
                        <td data-label="Month limit">
                          <LimitMeter
                            used={g.usedThisMonth}
                            limit={g.monthlyLimit}
                            unlimited={g.isUnlimited}
                            projected={g.projectedThisMonth}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
              {!top.length && <EmptyState title="No usage in the last 30 days." />}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Buddy dashboard: nyce.buddy usage, health, feedback backlog and alerts per environment, and what looks wrong. */
export function BuddyDashboardPage() {
  const res = useBuddySummary();
  const list = res.data ?? [];
  return (
    <Page
      title="Buddy"
      description="nyce.buddy per environment: questions, users, tokens and cost of the last 30 days, system health, the feedback backlog and open monitor alerts. Cached for a minute."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={res.loading}
          onClick={res.reload}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={res.error} />
      {!res.enabled && <EmptyState card title="No Buddy access" hint="Needs ophub:buddy:read." />}
      {res.enabled && !res.data && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {res.data && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <IssueList
            issues={buddyIssues(list)}
            title="Buddy issues"
            clear="No Buddy issues."
            testId="buddy-issues"
          />
          {list.map((s) => (
            <EnvironmentSection key={s.target.id} s={s} />
          ))}
          {!list.length && (
            <EmptyState card title="No nyce.buddy configured" hint="Set BuddyUrl on an AuthTargets entry." />
          )}
        </div>
      )}
    </Page>
  );
}
